import { useContext, useState, useEffect, useRef } from "react";
import { FaUserCircle } from "react-icons/fa";
import Notification from "../../components/UI/Notification";
import { AuthContext } from "../../contexts/AuthContext";
import { useNotifications } from "../../contexts/NotificationContext";
import TimeAgo from "../../utils/TimeAgo";
import axios from "axios";
import TodayDate from "./TodayDate";
import { getToken } from "../../utils/tokenUtils";
import ConfirmationModal from "./ConfirmationModal";
import Modal from "./Modal";

const Header = () => {
  const { user, logout, roles, logoutWarning, dismissLogoutWarning } =
    useContext(AuthContext);
  const {
    notifications,
    addNotification,
    fetchNotifications,
    readNotification,
    deleteNotification,
    readAllNotification,
  } = useNotifications();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [modal, setModal] = useState({ isOpen: false, action: null, id: null });
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("profile"); // 'profile' or 'password'
  const [formData, setFormData] = useState({
    old_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [showPassword, setShowPassword] = useState({
    old: false,
    new: false,
    confirm: false,
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [userDetailsModal, setUserDetailsModal] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const token = getToken();
  const dropdownRef = useRef(null);
  const notificationRef = useRef(null);

  const togglePassword = (field) => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const openChangePasswordModal = () => {
    setIsChangePasswordOpen(true);
    setIsSettingsOpen(false);
  };

  const closeChangePasswordModal = () => {
    setIsChangePasswordOpen(false);
    setFormData({
      old_password: "",
      new_password: "",
      confirm_password: "",
    });
    setPasswordErrors({});
  };

  const openSettingsModal = () => {
    setIsSettingsOpen(true);
    setActiveTab("profile");
    setDropdownOpen(false);
  };

  const closeSettingsModal = () => {
    setIsSettingsOpen(false);
    setActiveTab("profile");
    setPasswordErrors({});
    setFormData({
      old_password: "",
      new_password: "",
      confirm_password: "",
    });
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field when user types
    if (passwordErrors[name]) {
      setPasswordErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordErrors({});

    if (formData.new_password !== formData.confirm_password) {
      setPasswordErrors({
        confirm_password: "New Password and Confirm Password do not match!",
      });
      setNotification({
        message: "New Password and Confirm Password do not match!",
        type: "error",
      });
      return;
    }

    try {
      setLoginLoading(true);

      const response = await axios.post(
        "https://hrmspayroll.prepstripe.com/api/change-password",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.data.status) {
        setNotification({
          message: `${
            response.data.message || "Password changed successfully!"
          }`,
          type: "success",
        });
        closeSettingsModal();
        setIsChangePasswordOpen(false);
        setFormData({
          old_password: "",
          new_password: "",
          confirm_password: "",
        });
        setPasswordErrors({});
      } else {
        const errorMessage =
          response.data.message || "Failed to change password.";
        setPasswordErrors({ general: errorMessage });
        setNotification({
          message: errorMessage,
          type: "error",
        });
      }
    } catch (error) {
      console.error(error);
      const errorData = error.response?.data || {};

      // Handle field-specific errors (e.g., Laravel validation errors)
      if (errorData.errors && typeof errorData.errors === "object") {
        const fieldErrors = {};
        Object.entries(errorData.errors).forEach(([key, value]) => {
          // Map API field names to form field names
          const fieldName =
            key === "old_password" || key === "current_password"
              ? "old_password"
              : key === "new_password" || key === "password"
                ? "new_password"
                : key === "confirm_password" || key === "password_confirmation"
                  ? "confirm_password"
                  : key;
          fieldErrors[fieldName] = Array.isArray(value) ? value[0] : value;
        });
        setPasswordErrors(fieldErrors);
      } else {
        // General error message
        const errorMessage =
          errorData.message || "An error occurred while changing password.";
        setPasswordErrors({ general: errorMessage });
      }

      setNotification({
        message:
          errorData.message || "An error occurred while changing password.",
        type: "error",
      });
    } finally {
      setLoginLoading(false);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setNotificationOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const logoutMessage = localStorage.getItem("loginMessage");
    if (logoutMessage) {
      setNotification(logoutMessage);
      localStorage.removeItem("loginMessage");
    }
  }, []);

  // for notification dropdown
  const toggleNotification = () => {
    setNotificationOpen(!notificationOpen);
    setDropdownOpen(false); // Close profile dropdown if open
  };

  // for profile dropdown
  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
    setNotificationOpen(false); // Close notification dropdown if open
  };

  // Fetch user profile
  const fetchUserProfile = async () => {
    try {
      setLoadingProfile(true);
      const response = await axios.get(
        "https://hrmspayroll.prepstripe.com/api/profile",
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      // Handle API response structure
      if (response.data.success === true && response.data.profile) {
        setUserProfile(response.data.profile);
        setUserDetailsModal(true);
      } else {
        setNotification({
          message: response.data.message || "Failed to fetch user profile",
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
      setNotification({
        message:
          error.response?.data?.message ||
          error.message ||
          "An error occurred while fetching profile.",
        type: "error",
      });
    } finally {
      setLoadingProfile(false);
    }
  };

  // Handle clicking on user name in dropdown to open profile modal
  const handleUserNameClick = async () => {
    setDropdownOpen(false);
    await fetchUserProfile();
  };

  // logout function with delay
  const handleLogout = async () => {
    setLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 2000)); // additional delay
      const log_out = await logout(); // Call the logout function from AuthContext
      // console.log("Logout response: ", log_out);

      // Redirect or clear state here
    } catch (error) {
      setNotification("Error during logout");
    } finally {
      setLoading(false);
    }
  };

  // read all notifications
  const handleReadAllNotifications = async () => {
    try {
      await readAllNotification();
      setNotification({
        message: "Marked all notifications as read",
        type: "success",
      });
    } catch (error) {
      setNotification({ message: "Failed to delete campaign", type: "error" });
    }
  };

  return (
    <>
      {/* Logout Warning Banner */}
      {logoutWarning && (
        <div className="bg-yellow-500 dark:bg-yellow-600 text-white px-4 py-2 text-center text-sm font-medium relative z-[9999]">
          <div className="flex items-center justify-center gap-3">
            <span>{logoutWarning}</span>
            <button
              onClick={dismissLogoutWarning}
              className="ml-2 hover:bg-yellow-600 dark:hover:bg-yellow-700 rounded px-2 py-1 transition-colors"
              aria-label="Dismiss warning"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <header className="bg-white dark:bg-black shadow-sm text-white px-4 py-4 relative z-[9999]">
        <div className="flex flex-wrap items-center justify-between gap-2">
          {/* notification compo */}
          {notification && (
            <Notification
              message={notification.message}
              type={notification.type}
              onClose={() => setNotification(null)}
            />
          )}

          {/* Date + System Status */}
          <div className="hidden md:flex items-center  gap-3 text-sm">
            <div className="text-black dark:text-white font-poppins">
              <TodayDate />
            </div>
          </div>

          {/* Right Section */}
          <div className="flex flex-wrap items-center gap-3 md:gap-2 justify-start ml-auto ">
            {/* Notification Icon */}
            <div ref={notificationRef} className="relative">
              <div
                onClick={toggleNotification}
                className="p-2 rounded  border-[1px] border-transparent hover:border-gray-900 bg-gray-50 cursor-pointer"
              >
                <svg
                  className="w-5 h-5 text-black"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0" />
                </svg>
                {notifications.filter((n) => !n.read_at).length > 0 && (
                  <span className="absolute -top-2 -right-2 bg-blue-600 text-xs rounded-full px-1.5 py-[1.5px] font-semibold font-poppins">
                    {notifications.filter((n) => !n.read_at).length}
                  </span>
                )}
              </div>

              {/* Notification Dropdown */}
              {notificationOpen && (
                <div className="absolute right-0 mt-2 md:w-[300px] w-[289px] bg-white dark:bg-gray-900 border dark:border-gray-700 rounded-md shadow-lg z-[10000]">
                  <div className="flex justify-between px-4 py-2 border-b dark:border-gray-700">
                    <h3 className="text-sm font-medium dark:text-white text-black font-poppins">
                      Notifications
                    </h3>
                    {notifications.filter(
                      (notification) => notification.read_at === null,
                    ).length > 0 && (
                      <button
                        className="text-blue-500 text-xs font-semibold font-poppins"
                        onClick={handleReadAllNotifications}
                      >
                        Mark All as Read
                      </button>
                    )}
                  </div>

                  <ul className="max-h-64 overflow-y-auto font-poppins">
                    {notifications.filter(
                      (notification) => notification.read_at === null,
                    ).length > 0 ? (
                      notifications
                        .filter((notification) => notification.read_at === null) // Filter unread notifications
                        .map((notification) => (
                          <li
                            key={notification.id}
                            className="px-4 py-2 border-b dark:border-gray-700 flex justify-between items-center"
                          >
                            <div className="flex flex-col gap-y-1">
                              <p className="text-sm dark:text-white text-black">
                                {notification.data.title}
                              </p>
                              <div className="text-xs">
                                <TimeAgo timestamp={notification.created_at} />
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button
                                className="text-blue-500 text-xs"
                                onClick={() =>
                                  readNotification({
                                    notification_id: notification.id,
                                  })
                                }
                              >
                                Read
                              </button>
                              <button
                                className="text-red-500 text-xs"
                                onClick={() =>
                                  deleteNotification(notification.id)
                                }
                              >
                                Delete
                              </button>
                            </div>
                          </li>
                        ))
                    ) : (
                      <li className="px-4 py-2 text-center text-sm dark:bg-gray-900 text-black dark:text-white rounded-b-md">
                        No new notifications
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>

            {/* Profile Dropdown */}
            <div
              ref={dropdownRef}
              onClick={toggleDropdown}
              className="relative p-2 rounded border-[1px] border-transparent hover:border-gray-900 bg-gray-50 cursor-pointer"
            >
              {/* button */}
              <li
                title={`EMP ID - ${user.employee_id || ""}`}
                className="flex items-start gap-2 dark:hover:bg-gray-600 text-gray-800 dark:text-white rounded-md transition"
              >
                <FaUserCircle className="w-5 h-5 text-black" />
                <div className="flex flex-col">
                  <p className="text-sm font-medium  text-black">{user.name}</p>
                  {/* <p className="text-xs text-blue-500">EMP ID : {user.employee_id}</p> */}
                </div>
              </li>

              {/* dropdown card */}
              {dropdownOpen && (
                <div className="absolute font-poppins right-0 mt-[18px] w-48 bg-white dark:bg-gray-900 border p-1 dark:border-gray-700 shadow-lg z-[10000] rounded-md">
                  <ul>
                    <li
                      onClick={handleUserNameClick}
                      className="p-2 flex items-start gap-2 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-600 dark:text-white rounded-md transition cursor-pointer"
                    >
                      {/* <FaUserCircle className="w-5 h-5" /> */}
                      <div className="flex flex-col">
                        <p className="text-sm font-medium">{user.name}</p>
                        <p className="text-xs text-blue-500">{roles}</p>
                      </div>
                    </li>
                    <li
                      onClick={openSettingsModal}
                      className="p-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-600 dark:text-white rounded-md transition"
                    >
                      Settings
                    </li>
                  </ul>
                  <ul>
                    <li
                      // onClick={handleLogout}
                      onClick={() =>
                        setModal({ isOpen: true, action: "logout", id: null })
                      }
                      className="p-2 text-sm cursor-pointer hover:bg-red-100 dark:hover:bg-red-600 text-red-600 dark:text-white rounded-md transition"
                    >
                      Logout
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Optional logout loading overlay */}
        {loading && (
          <div className="fixed inset-0 z-50 bg-white font-poppins dark:bg-gray-900 bg-opacity-90 flex items-center justify-center">
            <div className="flex flex-col items-center justify-center gap-2">
              <div className="loader border-t-4 border-blue-500 rounded-full w-12 h-12 animate-spin mb-4 ml-5" />
              <p className="text-gray-700 dark:text-white">Logging out...</p>
            </div>
          </div>
        )}

        {/* User Profile Details Modal */}
        {userDetailsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-end md:items-center z-50 backdrop-blur-sm">
            <div className="bg-white font-poppins dark:bg-gray-950 dark:border dark:border-gray-700 rounded-t-3xl md:rounded-lg shadow-xl p-6 w-full max-w-md mx-auto relative transition-transform duration-300 transform md:animate-none animate-slideUp">
              {/* Close button */}
              <button
                onClick={() => setUserDetailsModal(false)}
                className="absolute top-4 right-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-2xl"
              >
                ×
              </button>

              {loadingProfile ? (
                <div className="flex flex-col items-center justify-center gap-2 py-8">
                  <div className="loader border-t-4 border-blue-500 rounded-full w-12 h-12 animate-spin" />
                  <p className="text-gray-700 dark:text-white text-sm">
                    Loading profile...
                  </p>
                </div>
              ) : userProfile ? (
                <div className="space-y-4">
                  <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">
                    User Profile
                  </h2>

                  {/* Profile Header */}
                  {/* Profile Header */}
                  <div className="flex items-center gap-4 pb-4 border-b dark:border-gray-700">
                    <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                      {((userProfile.name ?? "-").charAt &&
                        (userProfile.name ?? "-").charAt(0).toUpperCase()) ||
                        "U"}
                    </div>
                    <div className="flex-1">
                      <p className="text-lg font-semibold text-gray-800 dark:text-white">
                        {userProfile.name ?? "-"}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {userProfile.email ?? "-"}
                      </p>
                    </div>
                  </div>

                  {/* Profile Details */}
                  <div className="space-y-3">
                    <div className="flex justify-between items-start">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Employee ID:
                      </span>
                      <span className="text-sm text-gray-800 dark:text-white">
                        {userProfile.employee_id ?? "-"}
                      </span>
                    </div>

                    <div className="flex justify-between items-start">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Department:
                      </span>
                      <span className="text-sm text-gray-800 dark:text-white">
                        {userProfile.department_name ?? "-"}
                      </span>
                    </div>

                    <div className="flex justify-between items-start">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Designation:
                      </span>
                      <span className="text-sm text-gray-800 dark:text-white">
                        {userProfile.designation_name ?? "-"}
                      </span>
                    </div>

                    <div className="flex justify-between items-start">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Branch:
                      </span>
                      <span className="text-sm text-gray-800 dark:text-white">
                        {userProfile.branch_name ?? "-"}
                      </span>
                    </div>

                    <div className="flex justify-between items-start">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Manager (Primary Approver):
                      </span>
                      <span className="text-sm text-gray-800 dark:text-white">
                        {userProfile.manager_name ?? "-"}
                      </span>
                    </div>
                    <div className="flex justify-between items-start">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Secondary Approver:
                      </span>
                      <span className="text-sm text-gray-800 dark:text-white">
                        {userProfile.secondary_approver_name ?? "-"}
                      </span>
                    </div>

                    <div className="flex justify-between items-start">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        HOD:
                      </span>
                      <span className="text-sm text-gray-800 dark:text-white">
                        {userProfile.hod_name ?? "-"}
                      </span>
                    </div>

                    <div className="flex justify-between items-start">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Date of Joining:
                      </span>
                      <span className="text-sm text-gray-800 dark:text-white">
                        {userProfile.date_of_joining
                          ? new Date(
                              userProfile.date_of_joining,
                            ).toLocaleDateString()
                          : "-"}
                      </span>
                    </div>
                  </div>

                  {/* Close Button */}
                  <div className="flex justify-end pt-4 border-t dark:border-gray-700">
                    <button
                      onClick={() => setUserDetailsModal(false)}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-center text-gray-600 dark:text-gray-400 py-8">
                  Failed to load profile details.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Change password Modal */}
        {isChangePasswordOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-end md:items-center z-50 backdrop-blur-sm">
            <div className="bg-white font-poppins dark:bg-gray-950 dark:border dark:border-gray-700 rounded-t-3xl md:rounded-lg shadow-xl p-6 w-full max-w-md mx-auto relative transition-transform duration-300 transform md:animate-none animate-slideUp">
              <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">
                Change Password
              </h2>

              <form onSubmit={handleChangePassword} className="space-y-4">
                <div className="relative">
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Current Password{" "}
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    id="password"
                    name="old_password"
                    type={showPassword.old ? "text" : "password"}
                    value={formData.old_password}
                    onChange={handleInputChange}
                    maxLength={30}
                    minLength={8}
                    required
                    className={`w-full px-3 py-2 border dark:border-gray-700 text-black rounded-md focus:outline-none focus:border-gray-600 dark:bg-black dark:text-white ${
                      passwordErrors.old_password
                        ? "border-red-500 dark:border-red-500"
                        : ""
                    }`}
                  />
                  {passwordErrors.old_password && (
                    <p className="text-red-500 text-sm mt-1">
                      {passwordErrors.old_password}
                    </p>
                  )}
                  <span
                    onClick={() => togglePassword("old")}
                    className="absolute inset-y-0 right-3 top-6 flex items-center cursor-pointer text-gray-500 dark:text-gray-300"
                  >
                    {showPassword.old ? (
                      <svg
                        className="w-5 h-5"
                        data-slot="icon"
                        aria-hidden="true"
                        fill="none"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        ></path>
                      </svg>
                    ) : (
                      <svg
                        className="w-5 h-5"
                        data-slot="icon"
                        aria-hidden="true"
                        fill="none"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        ></path>
                        <path
                          d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        ></path>
                      </svg>
                    )}
                  </span>
                </div>
                <div className="relative">
                  <label
                    htmlFor="new_password"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    New Password <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    id="new_password"
                    type={showPassword.new ? "text" : "password"}
                    name="new_password"
                    value={formData.new_password}
                    onChange={handleInputChange}
                    required
                    maxLength={30}
                    minLength={8}
                    className={`w-full px-3 py-2 text-black border dark:border-gray-700 rounded-md focus:outline-none focus:border-gray-600 dark:bg-black dark:text-white ${
                      passwordErrors.new_password
                        ? "border-red-500 dark:border-red-500"
                        : ""
                    }`}
                  />
                  {passwordErrors.new_password && (
                    <p className="text-red-500 text-sm mt-1">
                      {passwordErrors.new_password}
                    </p>
                  )}
                  <span
                    onClick={() => togglePassword("new")}
                    className="absolute inset-y-0 right-3 top-6 flex items-center cursor-pointer text-gray-500 dark:text-gray-300"
                  >
                    {showPassword.new ? (
                      <svg
                        className="w-5 h-5"
                        data-slot="icon"
                        aria-hidden="true"
                        fill="none"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        ></path>
                      </svg>
                    ) : (
                      <svg
                        className="w-5 h-5"
                        data-slot="icon"
                        aria-hidden="true"
                        fill="none"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        ></path>
                        <path
                          d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        ></path>
                      </svg>
                    )}
                  </span>
                </div>
                <div className="relative">
                  <label
                    htmlFor="confirm_password"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Confirm New Password{" "}
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    id="confirm_password"
                    type={showPassword.confirm ? "text" : "password"}
                    name="confirm_password"
                    value={formData.confirm_password}
                    onChange={handleInputChange}
                    required
                    maxLength={30}
                    min={8}
                    className={`w-full px-3 py-2 text-black border dark:border-gray-700 rounded-md focus:outline-none focus:border-gray-600 dark:bg-black dark:text-white ${
                      passwordErrors.confirm_password
                        ? "border-red-500 dark:border-red-500"
                        : ""
                    }`}
                  />
                  {passwordErrors.confirm_password && (
                    <p className="text-red-500 text-sm mt-1">
                      {passwordErrors.confirm_password}
                    </p>
                  )}
                  <span
                    onClick={() => togglePassword("confirm")}
                    className="absolute inset-y-0 right-3 top-6 flex items-center cursor-pointer text-gray-500 dark:text-gray-300"
                  >
                    {showPassword.confirm ? (
                      <svg
                        className="w-5 h-5"
                        data-slot="icon"
                        aria-hidden="true"
                        fill="none"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        ></path>
                      </svg>
                    ) : (
                      <svg
                        className="w-5 h-5"
                        data-slot="icon"
                        aria-hidden="true"
                        fill="none"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        ></path>
                        <path
                          d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        ></path>
                      </svg>
                    )}
                  </span>
                </div>

                {/* General error message */}
                {passwordErrors.general && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
                    <p className="text-red-600 dark:text-red-400 text-sm">
                      {passwordErrors.general}
                    </p>
                  </div>
                )}

                <div className="flex justify-between space-x-2 pt-4">
                  <button
                    type="button"
                    onClick={closeChangePasswordModal}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`px-4 py-2 text-white rounded ${
                      loading ? "bg-blue-400" : "bg-blue-600 hover:bg-blue-700"
                    }`}
                    disabled={loading}
                  >
                    {loading ? "Changing..." : "Change Password"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Settings Modal */}
        <Modal
          isOpen={isSettingsOpen}
          onRequestClose={closeSettingsModal}
          title="Settings"
        >
          <div className="space-y-3">
            {/* Tabs */}
            <div className="flex border-b border-gray-200 dark:border-gray-700">
              <button
                onClick={() => handleTabChange("profile")}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === "profile"
                    ? "border-blue-500 text-blue-600 dark:text-blue-400"
                    : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                }`}
              >
                Profile
              </button>
              <button
                onClick={() => handleTabChange("password")}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === "password"
                    ? "border-blue-500 text-blue-600 dark:text-blue-400"
                    : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                }`}
              >
                Change Password
              </button>
            </div>

            {/* Tab Content */}
            {activeTab === "profile" && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Profile Information
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={user.name || ""}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Employee ID
                    </label>
                    <input
                      type="text"
                      value={user.employee_id || ""}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={user.email || ""}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Role
                    </label>
                    <input
                      type="text"
                      value={roles || ""}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>

                <div className="pt-4">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                    Contact your administrator to update profile information.
                  </p>

                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setNotification({
                          message: "Profile information updated successfully!",
                          type: "success",
                        });
                      }}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 font-medium text-white rounded-md transition-colors"
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "password" && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Change Password
                </h3>

                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div className="relative">
                    <label
                      htmlFor="old_password"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                      Current Password{" "}
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      id="old_password"
                      name="old_password"
                      type={showPassword.old ? "text" : "password"}
                      value={formData.old_password}
                      onChange={handleInputChange}
                      maxLength={30}
                      minLength={8}
                      required
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none dark:bg-gray-800 text-gray-900 dark:text-white ${
                        passwordErrors.old_password
                          ? "border-red-500 dark:border-red-500 focus:border-red-500"
                          : "border-gray-300 dark:border-gray-600 focus:border-blue-500"
                      }`}
                    />
                    {passwordErrors.old_password && (
                      <p className="text-red-500 text-sm mt-1">
                        {passwordErrors.old_password}
                      </p>
                    )}
                    <span
                      onClick={() => togglePassword("old")}
                      className="absolute inset-y-0 right-3 top-6 flex items-center cursor-pointer text-gray-500 dark:text-gray-300"
                    >
                      {showPassword.old ? (
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                      )}
                    </span>
                  </div>

                  <div className="relative">
                    <label
                      htmlFor="new_password"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                      New Password <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      id="new_password"
                      type={showPassword.new ? "text" : "password"}
                      name="new_password"
                      value={formData.new_password}
                      onChange={handleInputChange}
                      required
                      maxLength={30}
                      minLength={8}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none dark:bg-gray-800 text-gray-900 dark:text-white ${
                        passwordErrors.new_password
                          ? "border-red-500 dark:border-red-500 focus:border-red-500"
                          : "border-gray-300 dark:border-gray-600 focus:border-blue-500"
                      }`}
                    />
                    {passwordErrors.new_password && (
                      <p className="text-red-500 text-sm mt-1">
                        {passwordErrors.new_password}
                      </p>
                    )}
                    <span
                      onClick={() => togglePassword("new")}
                      className="absolute inset-y-0 right-3 top-6 flex items-center cursor-pointer text-gray-500 dark:text-gray-300"
                    >
                      {showPassword.new ? (
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                      )}
                    </span>
                  </div>

                  <div className="relative">
                    <label
                      htmlFor="confirm_password"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                      Confirm New Password{" "}
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      id="confirm_password"
                      type={showPassword.confirm ? "text" : "password"}
                      name="confirm_password"
                      value={formData.confirm_password}
                      onChange={handleInputChange}
                      required
                      maxLength={30}
                      minLength={8}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none dark:bg-gray-800 text-gray-900 dark:text-white ${
                        passwordErrors.confirm_password
                          ? "border-red-500 dark:border-red-500 focus:border-red-500"
                          : "border-gray-300 dark:border-gray-600 focus:border-blue-500"
                      }`}
                    />
                    {passwordErrors.confirm_password && (
                      <p className="text-red-500 text-sm mt-1">
                        {passwordErrors.confirm_password}
                      </p>
                    )}
                    <span
                      onClick={() => togglePassword("confirm")}
                      className="absolute inset-y-0 right-3 top-6 flex items-center cursor-pointer text-gray-500 dark:text-gray-300"
                    >
                      {showPassword.confirm ? (
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                      )}
                    </span>
                  </div>

                  {/* General error message */}
                  {passwordErrors.general && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
                      <p className="text-red-600 dark:text-red-400 text-sm">
                        {passwordErrors.general}
                      </p>
                    </div>
                  )}

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="submit"
                      className={`px-4 py-2 text-white rounded-md font-medium transition-colors ${
                        loginLoading
                          ? "bg-blue-400 cursor-not-allowed"
                          : "bg-blue-600 hover:bg-blue-700"
                      }`}
                      disabled={loginLoading}
                    >
                      {loginLoading ? "Changing..." : "Change Password"}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </Modal>

        <ConfirmationModal
          isOpen={modal.isOpen}
          onClose={() => setModal({ isOpen: false, action: null, id: null })}
          onConfirm={handleLogout}
          message={`Are you sure you want to ${modal.action}?`}
        />
      </header>
    </>
  );
};

export default Header;
